import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SaveButton,
  TextField,
  useAutocomplete,
} from "@pankod/refine-mui";
import {
  Controller,
  UseModalFormReturnType,
} from "@pankod/refine-react-hook-form";

import { IEmployee, IProduct } from "interfaces";

export const ProductOutModal: React.FC<UseModalFormReturnType> = ({
  saveButtonProps,
  modal: { visible, close, title },
  register,
  control,
  setValue,
  formState: { errors },
}) => {
  const [currentProduct, setCurrentProduct] = useState<IProduct | null>(null);
  const { autocompleteProps: productsAutocompleteProps } =
    useAutocomplete<IProduct>({
      resource: "products",
    });

  const { autocompleteProps: employeesAutocompleteProps } =
    useAutocomplete<IEmployee>({
      resource: "employees",
      filters: [{ field: "active", operator: "eq", value: true }],
      onSearch: (search) => [
        {
          field: "name",
          operator: "contains",
          value: search,
        },
      ],
    });

  const closeModal = () => {
    close();
    setCurrentProduct(null);
    setValue("product", null);
    setValue("employee", null);
  };

  return (
    <Dialog
      open={visible}
      onClose={closeModal}
      PaperProps={{ sx: { minWidth: 500 } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Controller
            control={control}
            name="employee"
            rules={{ required: "El campo empleado es obligatorio." }}
            defaultValue={null as any}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...employeesAutocompleteProps}
                  {...field}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  getOptionLabel={(item) => {
                    return (
                      employeesAutocompleteProps?.options?.find(
                        (p) => p?.id?.toString() === item?.id?.toString()
                      )?.name ?? ""
                    );
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return (
                      value === undefined ||
                      option.id.toString() === value.id.toString()
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Empleado"
                      margin="normal"
                      variant="outlined"
                      error={!!errors.employee}
                      helperText={errors.employee?.message?.toString()}
                      required
                    />
                  )}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="product"
            rules={{ required: "El campo tipo de producto es obligatorio." }}
            defaultValue={null as any}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...productsAutocompleteProps}
                  {...field}
                  onChange={(_, value) => {
                    field.onChange(value);
                    setCurrentProduct(value);
                  }}
                  getOptionLabel={(item) => {
                    const label = productsAutocompleteProps?.options?.find(
                      (p) => p?.id?.toString() === item?.id?.toString()
                    );

                    if (!label?.name || !label?.code) return "";
                    return `${label?.code} - ${label?.name}`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return (
                      value === undefined ||
                      option.id.toString() === value.id.toString()
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de producto"
                      margin="normal"
                      variant="outlined"
                      error={!!errors.product}
                      helperText={errors.product?.message?.toString()}
                      autoFocus
                      required
                    />
                  )}
                />
              );
            }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Medida"
            value={currentProduct?.measure ?? ""}
          />
          <TextField
            {...register("qty", {
              required: "El campo cantidad es obligatorio.",
            })}
            error={!!errors?.qty}
            helperText={errors.qty?.message?.toString()}
            margin="normal"
            fullWidth
            id="qty"
            label="Cantidad"
            name="qty"
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Cancel</Button>
        <SaveButton {...saveButtonProps} />
      </DialogActions>
    </Dialog>
  );
};
